import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { getCountryFlag } from '../utils/countries';
import { validateLeaguePreferenceSelection, sanitizeLeaguePreferenceState } from '../utils/leaguePreference';
import {
  clashWinnerIconToApi,
  sanitizeClashWinnerIcon
} from '../utils/clashWinnerIcon';
import { getClashWinnerBadgeEquipOptions } from '../utils/clashWinnerBadgeEquip';
import ProfileInventoryAchievements from '../components/ProfileInventoryAchievements';
import LeaguePreferenceEditor from '../components/LeaguePreferenceEditor';
import './Profile.css';
import './Betting.css';
import './Account.css';

const Account = () => {
  const { user, logout, updateToken } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [leaguePreference, setLeaguePreference] = useState([]);
  const [clashWinnerIcon, setClashWinnerIcon] = useState('crown');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [saveError, setSaveError] = useState('');
  const [badgeLoading, setBadgeLoading] = useState(false);
  const [badgeMessage, setBadgeMessage] = useState('');
  const [badgeError, setBadgeError] = useState('');

  const inventory = data?.inventory ?? user?.inventory ?? {};

  const badgeEquipOptions = useMemo(
    () => getClashWinnerBadgeEquipOptions(inventory),
    [inventory]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await api.get('/me');
        if (!cancelled) {
          setData(res.data);
          const preference = sanitizeLeaguePreferenceState(
            res.data.leaguePreference,
            res.data.leagueDenied
          );
          setLeaguePreference(preference.leaguePreference);
          setClashWinnerIcon(clashWinnerIconToApi(res.data.clashWinnerIcon));
        }
      } catch (err) {
        if (cancelled) return;
        if (err.response?.status === 401) {
          setError('Session expired. Please log in again.');
        } else {
          setError(err.response?.data?.error || 'Could not load your account');
        }
        setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const username = data?.username ?? '';

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleSaveLeaguePreference = async () => {
    const validationError = validateLeaguePreferenceSelection(leaguePreference);
    if (validationError) {
      setSaveError(validationError);
      setSaveMessage('');
      return;
    }

    setSaveLoading(true);
    setSaveError('');
    setSaveMessage('');
    try {
      const res = await api.put('/me/league-preference', { leaguePreference });
      const nextPreference = res.data.leaguePreference;
      const nextDenied = res.data.leagueDenied || [];
      setLeaguePreference(nextPreference);
      setData((prev) => (prev
        ? { ...prev, leaguePreference: nextPreference, leagueDenied: nextDenied }
        : prev));
      if (res.data.token) {
        updateToken(res.data.token, { lp: nextPreference, ld: nextDenied });
      }
      setSaveMessage('League preferences saved.');
    } catch (err) {
      setSaveError(err.response?.data?.error || 'Could not save league preferences');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleEquipClashWinnerIcon = async (nextIcon) => {
    const normalized = clashWinnerIconToApi(nextIcon);
    if (normalized === clashWinnerIcon) return;

    setBadgeLoading(true);
    setBadgeError('');
    setBadgeMessage('');
    try {
      const res = await api.put('/me/clash-winner-icon', { clashWinnerIcon: normalized });
      const equipped = clashWinnerIconToApi(res.data.clashWinnerIcon);
      setClashWinnerIcon(equipped);
      if (res.data.token) {
        updateToken(res.data.token, {
          clashWinnerIcon: equipped,
          w: sanitizeClashWinnerIcon(equipped)
        });
      }
      setBadgeMessage('Clash winner badge saved.');
    } catch (err) {
      setBadgeError(err.response?.data?.error || 'Could not save clash winner badge');
    } finally {
      setBadgeLoading(false);
    }
  };

  const leaguePreferenceDirty = useMemo(() => {
    const saved = Array.isArray(data?.leaguePreference) ? data.leaguePreference : [];
    if (saved.length !== leaguePreference.length) return true;
    return saved.some((code, index) => code !== leaguePreference[index]);
  }, [data?.leaguePreference, leaguePreference]);

  return (
    <div className="betting-page profile-page account-page">
      <header className="betting-header">
        <p className="profile-back">
          <Link to="/" className="profile-back-link">
            ← Home
          </Link>
        </p>
        <h1 className="betting-title">Your account</h1>
        <p className="betting-subtitle">Manage your profile and game progress</p>
      </header>

      {loading && <div className="sc-loading">Loading…</div>}
      {!loading && error && <div className="sc-error">{error}</div>}

      {!loading && data && (
        <>
          <div className="sc-card profile-hero account-hero">
            <div className="profile-hero-name">
              <span className="profile-username">{data.username}</span>
              {data.country ? (
                <span className="profile-flag" title={data.country}>
                  {getCountryFlag(data.country)}
                </span>
              ) : null}
            </div>
            <dl className="account-details">
              <div className="account-detail">
                <dt>Email</dt>
                <dd>{data.email}</dd>
              </div>
              <div className="account-detail">
                <dt>Country</dt>
                <dd>{data.country || '—'}</dd>
              </div>
              <div className="account-detail">
                <dt>Coin balance</dt>
                <dd className="account-detail-coins">
                  <img src="/coin.svg" alt="" className="account-coin-icon" />
                  <span>{data.coinBalance ?? 0}</span>
                </dd>
              </div>
              <div className="account-detail">
                <dt>Prize coins earned</dt>
                <dd>
                  <strong>{Number(data.prizeCoins) || 0}</strong>
                </dd>
              </div>
            </dl>
            <div className="account-links">
              <Link to={`/profile/${encodeURIComponent(username)}`} className="account-public-link">
                View public profile
              </Link>
              <Link to="/about" className="account-public-link">
                About ScoreClash
              </Link>
            </div>
            <button type="button" className="account-logout-btn" onClick={handleLogout}>
              Logout
            </button>
          </div>

          <section className="sc-card account-league-pref">
            <div className="account-league-pref__header">
              <h2 className="account-league-pref__title">Clash winner badge</h2>
            </div>
            <p className="account-clash-badge__hint">
              Shown next to your name when you win a clash duel. Applies on your next bet save.
            </p>
            <div className="account-clash-badge__options" role="group" aria-label="Clash winner badge">
              {badgeEquipOptions.map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  className={
                    'account-clash-badge__option' +
                    (clashWinnerIcon === opt.apiValue ? ' account-clash-badge__option--selected' : '')
                  }
                  onClick={() => handleEquipClashWinnerIcon(opt.apiValue)}
                  disabled={badgeLoading}
                  aria-pressed={clashWinnerIcon === opt.apiValue}
                >
                  <img src={opt.src} alt="" width={28} height={28} />
                  <span>{opt.label}</span>
                </button>
              ))}
            </div>
            {badgeMessage && (
              <p className="account-league-pref__message account-league-pref__message--success">
                {badgeMessage}
              </p>
            )}
            {badgeError && (
              <p className="account-league-pref__message account-league-pref__message--error">
                {badgeError}
              </p>
            )}
          </section>

          <section className="sc-card account-league-pref">
            <div className="account-league-pref__header">
              <h2 className="account-league-pref__title">League preferences</h2>
              <button
                type="button"
                className="account-league-pref__save"
                onClick={handleSaveLeaguePreference}
                disabled={saveLoading || !leaguePreferenceDirty}
              >
                {saveLoading ? 'Saving…' : 'Save preferences'}
              </button>
            </div>
            <LeaguePreferenceEditor
              value={leaguePreference}
              onChange={setLeaguePreference}
              disabled={saveLoading}
            />
            {saveMessage && <p className="account-league-pref__message account-league-pref__message--success">{saveMessage}</p>}
            {saveError && <p className="account-league-pref__message account-league-pref__message--error">{saveError}</p>}
          </section>

          <ProfileInventoryAchievements
            inventory={data?.inventory ?? user?.inventory ?? {}}
            achievements={data.achievements}
          />
        </>
      )}
    </div>
  );
};

export default Account;
